import { Panneau } from "../admin/ui.tsx";
import { ROLES_UI } from "../app/config.ts";
import { useApp } from "../app/state.tsx";
import { Lien } from "../lib/router.tsx";

/** Briques partagées des espaces privés (B0 : badge, documents ; planning ; rencontres). */

export function Badge(): React.ReactElement {
  const app = useApp();
  const nom = app.compte || "Compte démo";
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
      <div className="card" style={{ width: 260, overflow: "hidden" }}>
        <div style={{ background: "var(--accent)", color: "#fff", padding: "14px 16px" }}>
          <div className="eyebrow" style={{ color: "#ffffffcc" }}>{app.edition.optionLabel}</div>
          <div className="display" style={{ fontSize: 18, color: "#fff" }}>Hopitech</div>
        </div>
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10, alignItems: "center", textAlign: "center" }}>
          <div style={{ width: 92, height: 92, borderRadius: 10, background: "repeating-conic-gradient(var(--ink) 0% 25%, transparent 0% 50%) 50% / 12px 12px", opacity: 0.85 }} />
          <div>
            <div className="display" style={{ fontSize: 18 }}>{nom}</div>
            <div style={{ fontSize: 12.5, color: "var(--muted)" }}>{ROLES_UI.find((r) => r.id === app.role)?.label}</div>
          </div>
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 240, display: "flex", flexDirection: "column", gap: 10 }}>
        <p style={{ margin: 0, color: "var(--muted)", fontSize: 13.5, maxWidth: "48ch" }}>Votre badge est <strong>régénéré à la demande</strong> : il reflète toujours vos dernières données. Le QR est scanné en public — il identifie un rattachement, jamais une personne en clair.</p>
        <div style={{ display: "flex", gap: 8 }}>
          <a className="btn btn-primary" href="/api/badge" style={{ textDecoration: "none" }}>Télécharger mon badge</a>
          <button className="btn btn-ghost">Recevoir par e-mail</button>
        </div>
      </div>
    </div>
  );
}

export function Docs(): React.ReactElement {
  const docs = [
    { n: "Convention de stage", d: "prête" as const },
    { n: "Attestation de présence", d: "après le salon" as const },
    { n: "Convocation", d: "prête" as const },
    { n: "Badge", d: "prête" as const },
  ];
  return (
    <Panneau titre="Mes documents" sous="Régénérés à partir de vos données. Diffusion par lien signé expirant.">
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {docs.map((doc) => (
          <div key={doc.n} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "11px 13px", border: "1px solid var(--line)", borderRadius: 10 }}>
            <span style={{ fontWeight: 600, fontSize: 13.5 }}>{doc.n}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span className={`pill pill-${doc.d === "prête" ? "ok" : "muted"}`} style={{ textTransform: "none", fontFamily: "var(--sans)", fontSize: 11.5 }}>{doc.d}</span>
              <button className="btn btn-ghost" style={{ padding: "5px 11px", fontSize: 12 }} disabled={doc.d !== "prête"}>Télécharger</button>
            </div>
          </div>
        ))}
      </div>
    </Panneau>
  );
}

export function Planning(): React.ReactElement {
  const jours = [
    { j: "Jeudi 24 mars", items: [["09:30", "Ouverture & café"], ["10:00", "Maintenance biomédicale connectée · Salle A"], ["14:00", "Sobriété énergétique · Salle B"]] },
    { j: "Vendredi 25 mars", items: [["10:00", "Qualité de l'air en bloc · Salle A"], ["11:30", "Table ronde ingénierie · Auditorium"]] },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 720 }}>
      {jours.map((d) => (
        <Panneau key={d.j} titre={d.j}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {d.items.map(([h, t]) => (
              <div key={h} style={{ display: "flex", gap: 14, padding: "10px 0", borderBottom: "1px solid var(--line)" }}>
                <span className="mono" style={{ color: "var(--accent)", fontSize: 12.5, width: 48 }}>{h}</span>
                <span style={{ fontSize: 13.5 }}>{t}</span>
              </div>
            ))}
          </div>
        </Panneau>
      ))}
    </div>
  );
}

export function Rencontres(): React.ReactElement {
  const vus = [
    ["16:40", "DELABIE", "Sanitaires sécurisés"],
    ["15:10", "IDEX", "Efficacité énergétique"],
    ["14:22", "APAVE", "Contrôle réglementaire"],
    ["11:05", "ADOPALE", "Traitement de l'eau"],
  ];
  return (
    <Panneau titre="Exposants rencontrés" sous="Remontés par les scans du salon, classés par moment.">
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {vus.map(([h, e, s]) => (
          <div key={h} style={{ display: "flex", alignItems: "center", gap: 14, padding: "11px 0", borderBottom: "1px solid var(--line)" }}>
            <span className="mono" style={{ color: "var(--faint)", fontSize: 12, width: 44 }}>{h}</span>
            <span style={{ fontWeight: 600, width: 120 }}>{e}</span>
            <span style={{ fontSize: 13, color: "var(--muted)" }}>{s}</span>
          </div>
        ))}
      </div>
    </Panneau>
  );
}

export function RoleDashboard(): React.ReactElement {
  const app = useApp();
  const label = ROLES_UI.find((r) => r.id === app.role)?.label ?? "Mon espace";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 940 }}>
      <div className="card" style={{ padding: 22, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <h2 className="display" style={{ fontSize: 18, margin: "0 0 4px" }}>Bienvenue sur votre espace {label.toLowerCase()}</h2>
          <p style={{ margin: 0, color: "var(--muted)", fontSize: 13.5 }}>Édition {app.edition.optionLabel} · {app.edition.ville} · {app.edition.dates}</p>
        </div>
        <Lien vers={app.role === "stagiaire" ? "/inscription/stagiaire" : app.role === "visiteur" ? "/inscription/visiteur" : "#"} className="btn btn-primary" style={{ textDecoration: "none" }}>Mon inscription →</Lien>
      </div>
      <Badge />
      <Docs />
    </div>
  );
}
